/**
 * Facebook Pixel Configuration
 *
 * This file contains configuration settings for Facebook Pixel integration.
 *
 * To configure Facebook Pixel:
 * 1. Set the ALGOTECH_FB_PIXEL_ID environment variable in your .env files
 * 2. The pixel will automatically be initialized when the app loads
 * 3. PageView events are tracked automatically on navigation
 *
 * Environment variables:
 * - ALGOTECH_FB_PIXEL_ID: Your Facebook Pixel ID (e.g., "123456789012345")
 *
 * Usage:
 * - The MetaPixelProvider is already configured in src/app/app.tsx
 * - Use fbEvent() function to track standard Facebook events
 * - The pixel is disabled in development mode (__IS_DEV__ === true)
 */

/**
 * Advanced Matching parameters for Meta Pixel
 * These parameters help improve conversion tracking by matching website events with Facebook users
 * Values are automatically hashed by Facebook using SHA-256
 */
export interface AdvancedMatchingParams {
    em?: string; // Email
    fn?: string; // First name
    ln?: string; // Last name
    ph?: string; // Phone
    external_id?: string; // External ID
    ge?: string; // Gender (f or m)
    db?: string; // Date of birth (YYYYMMDD)
    ct?: string; // City
    st?: string; // State
    zp?: string; // Zip code
    country?: string; // Country code
}

declare global {
    interface Window {
        fbq?: (...args: unknown[]) => void;
    }
}

export const FACEBOOK_PIXEL_CONFIG = {
    // Facebook Pixel script URL
    scriptUrl: 'https://connect.facebook.net/en_US/fbevents.js',

    // Tracking image URL for noscript fallback
    noscriptUrl: 'https://www.facebook.com/tr',

    // Whether to enable pixel in development
    enableInDev: false,

    // Default events that are automatically tracked
    autoTrackEvents: {
        pageView: true,
        navigation: true,
    },

    // Pixel initialization timeout (milliseconds)
    initTimeout: 10000,

    // Retry interval for pixel initialization (milliseconds)
    retryInterval: 100,
} as const;

/**
 * Get the Facebook Pixel ID from environment variables
 */
export function getPixelId(): string | undefined {
    return typeof process !== 'undefined'
        ? // eslint-disable-next-line
          process.env['ALGOTECH_FB_PIXEL_ID']
        : undefined;
}

/**
 * Check if Facebook Pixel tracking is enabled
 */
export function isTrackingEnabled(): boolean {
    const pixelId = getPixelId();
    const isProduction = !__IS_DEV__;

    return Boolean(
        pixelId && (isProduction || FACEBOOK_PIXEL_CONFIG.enableInDev),
    );
}
